'use client';

import { type CSSProperties, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { SGhostButton } from '../SGhostButton';

/**
 * @internal
 * 모달·드로어 상단 제목 영역 공통 props.
 */
export interface SOverlayHeaderProps {
  /** 시각 제목 */
  title?: ReactNode;
  /** 제목 오른쪽 보조 설명 */
  description?: ReactNode;
  /** 닫기 버튼 표시 */
  showClose?: boolean;
  /** 닫기 버튼 클릭 */
  onClose?: () => void;
  /** 외부 absolute 닫기 버튼 공간 확보 */
  reserveCloseSpace?: boolean;
  className?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  style?: CSSProperties;
}

/**
 * SOverlayHeader — overlay 계열 내부 전용 헤더.
 *
 * @internal
 * SActionModal/SDrawer 의 제목 영역만 공통화한다. 패키지 public export 에 추가하지 말 것.
 */
export function SOverlayHeader({
  title,
  description,
  showClose = false,
  onClose,
  reserveCloseSpace = false,
  className,
  titleClassName,
  descriptionClassName,
  style,
}: SOverlayHeaderProps) {
  const hasDescription = description != null && description !== '';

  return (
    <header
      className={cn(
        'relative flex w-full flex-shrink-0 items-center',
        'gap-[var(--cmp-overlay-header-gap)] bg-[var(--cmp-overlay-header-bg)] py-[var(--cmp-overlay-header-paddingY)] pl-[var(--cmp-overlay-header-paddingX)]',
        showClose || reserveCloseSpace
          ? 'pr-[calc(var(--cmp-overlay-header-paddingX)+20px)]'
          : 'pr-[var(--cmp-overlay-header-paddingX)]',
        className,
      )}
      style={style}
    >
      <h2
        className={cn(
          'm-0 overflow-hidden text-ellipsis whitespace-nowrap',
          'text-[16px] font-bold leading-[26px]',
          titleClassName,
        )}
        style={{ color: 'var(--cmp-overlay-header-title-color)' }}
      >
        {title}
      </h2>
      {hasDescription && (
        <div
          className={cn(
            'flex min-w-0 flex-1 items-center gap-sd-2 text-[12px] font-normal leading-[20px] text-[color:var(--color-grey-65)]',
            descriptionClassName,
          )}
        >
          {description}
        </div>
      )}
      {showClose && (
        <SGhostButton
          icon="close"
          ariaLabel="닫기"
          onClick={() => onClose?.()}
          className="absolute right-[12px] top-[12px]"
        />
      )}
    </header>
  );
}
